import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, MapPin, BatteryCharging, ListChecks, Sparkles, ArrowRight, ArrowDown, Star, Check, ShieldCheck, Users, Flame, CloudSun, Navigation, Euro, Mail, MessageCircleHeart, Bike, Fuel, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ThemeToggle } from "../components/ThemeToggle";
import { Typewriter } from "../components/landing/Typewriter";
import { ExitIntentPopup } from "../components/landing/ExitIntentPopup";
import { ComparisonTable } from "../components/landing/ComparisonTable";
import { StickyMobileCta } from "../components/landing/StickyMobileCta";
import { ShowcaseTrip } from "../components/landing/ShowcaseTrip";
import { usePageMeta } from "../lib/seo";

const SHARED_FEATURES = [
  {
    id: "dagboek",
    kicker: "Reisdagboek",
    title: "Herbeleef elke kilometer.",
    text: "Spreek je herinneringen in — AI maakt er een vlot reisverhaal van. Foto's, video's en een deelbare trip-samenvatting waar familie live mee kan volgen.",
    img: "/media/journal-moment.png",
    align: "left",
  },
  {
    id: "kosten",
    kicker: "Kosten verdelen",
    title: "Wie betaalt wat? Geregeld.",
    text: "Tricount-stijl kostenverdeling, ingebouwd: iedereen voegt onderweg uitgaven toe en VoltRoute rekent uit wie wie moet terugbetalen. Deel het saldo met één tik via WhatsApp.",
    img: "/media/split-costs.jpg",
    align: "right",
  },
  {
    id: "samen",
    kicker: "Samen beleven",
    title: "Thuisblijvers reizen mee.",
    text: "Familie en vrienden reageren op elk reismoment met antwoorden en emoji's. Jij en je copiloot krijgen meteen een mailtje — en antwoorden direct, zonder in te loggen.",
    img: "/media/family-follow.jpg",
    align: "left",
  },
];

const SHARED_BENTO = [
  { icon: MapPin, title: "Dag-per-dag route", text: "Relaxte dagetappes met overnachtingen, activiteiten en eetplekken." },
  { icon: Sparkles, title: "AI-reisassistent", text: "Stel vragen en verfijn je route onderweg — aangedreven door GPT-4o." },
  { icon: Navigation, title: "GPX import & export", text: "Importeer je eigen route (hele trip of per dag). Of exporteer naar je navigatie." },
  { icon: MessageCircleHeart, title: "Reacties & emoji's", text: "Thuisblijvers reageren per dag of per reismoment — met antwoorden en WhatsApp-stijl emoji's." },
  { icon: Mail, title: "Direct antwoorden via e-mail", text: "Nieuwe reactie? Bestuurder en copiloot krijgen een mail en antwoorden meteen, zonder in te loggen." },
];

const TESTIMONIALS = {
  sofie: { name: "Sofie & Ward", car: "Kia EV6 · Ardennen & Elzas", text: "Eindelijk geen vijf apps meer nodig. De laadstops klopten perfect en oma volgde ons dagboek elke avond mee." },
  janssens: { name: "Familie Janssens", car: "Tesla Model Y · Zuid-Frankrijk", text: "Met twee kinderen achterin wil je zekerheid. De live modus zei ons exact wanneer we zouden aankomen — en we zaten er 4 minuten naast." },
  peter: { name: "Peter V.", car: "Polestar 2 · Noorwegen", text: "De budgetmodule voorspelde onze laadkosten verrassend accuraat. De trip kostte €23 minder dan geraamd." },
  kris: { name: "Kris V.", car: "BMW R 1250 GS · Vogezen", text: "Vijf sterren bochtigheids-score en de tankstops klopten exact. Eindelijk een planner die snapt wat motorrijders willen." },
  jef: { name: "Jef & Marc", car: "CFMoto MT 800 Explorer · Dolomieten", text: "Drie dagen Dolomieten vol vijf-sterren bochten. Tankstops kwamen precies op tijd en 's avonds stond het reisverslag al klaar voor het thuisfront." },
};

const MODES = {
  ev: {
    accentText: "text-[#74C69D]",
    btn: "bg-primary hover:bg-[#1B4332]",
    heroGlow: "shadow-[0_0_25px_rgba(116,198,157,0.25)] hover:shadow-[0_0_40px_rgba(116,198,157,0.4)]",
    meta: ["VoltRoute — Roadtrip-planner voor EV", "Route, laadstops, overnachtingen, budget en paklijst in één keer gepland — afgestemd op jouw elektrische auto. Familie volgt live mee via het reisdagboek. Start gratis."],
    kicker: "Elektrisch op roadtrip · 250.000+ laadpunten",
    phrases: ["Plan je elektrische roadtrip.", "Laadstops. Zonder stress.", "Geen range anxiety. Puur avontuur.", "Rij. Laad. Herbeleef."],
    sub: "Route, laadstops, overnachtingen, budget en reisdagboek — alles in één plan, afgestemd op jouw EV.",
    trust: (fmt, stats) => `4.8/5 — vertrouwd door ${fmt(stats.riders)} EV- & motorrijders · geen creditcard nodig`,
    stats: (fmt, stats) => [[fmt(stats.trips), "trips gepland"], [fmt(stats.vehicles), "EV- & moto-modellen in database"], ["250.000+", "laadpunten via OpenChargeMap"], ["4.8/5", "gemiddelde score"]],
    showcase: {
      testid: "mg-showcase",
      img: "/media/cyberster-scenic.png",
      alt: "Witte MG Cyberster op een kustweg",
      kicker: "MG Cyberster · en 1.281 andere EV's",
      title: "Jouw auto. Jouw route.",
      text: "Van MG Cyberster tot Tesla Model Y — VoltRoute rekent met de échte verbruikscijfers van jouw EV, het weer en jouw rijstijl.",
      cta: "Check of jouw EV erbij staat",
      curvePill: false,
    },
    features: [
      {
        id: "laadstops",
        kicker: "Slimme laadstops",
        title: "Laden waar het jou uitkomt.",
        text: "Automatisch geplande laadstops via 250.000+ echte laadpunten, afgestemd op jouw EV en batterijbuffer. Voorkeur voor Tesla, Ionity of Fastned? Eén tik.",
        img: "/media/charging-scenic.png",
        align: "left",
      },
      {
        id: "live",
        kicker: "Live modus",
        title: "Rijd met live inzicht.",
        text: "Waze-stijl voortgang, ETA op de minuut, automatische herplanning bij een lage batterij en bezienswaardigheden die je onderweg passeert.",
        img: "/media/night-nav.png",
        align: "right",
      },
      ...SHARED_FEATURES,
    ],
    bento: [
      SHARED_BENTO[0],
      { icon: Bike, title: "Moto-modus", text: "Bochtige & schilderachtige motoroutes met tankstops, moto-paklijst en regenwaarschuwing per rijdag." },
      SHARED_BENTO[1],
      { icon: Euro, title: "Budget & laadkosten", text: "Raming vooraf, werkelijke kosten bijhouden per dag." },
      { icon: ListChecks, title: "Paklijst op maat", text: "Op basis van tripduur, seizoen en reisgezelschap, incl. EV- of moto-checklist." },
      { icon: CloudSun, title: "Weer-impact op bereik", text: "Realistisch bereik per dag op basis van de weersvoorspelling." },
      SHARED_BENTO[2],
      SHARED_BENTO[3],
      SHARED_BENTO[4],
      { icon: BatteryCharging, title: "Volgende laadstop in beeld", text: "De ETA-balk op de kaart toont live je volgende laadstop — naam, afstand en laadtijd." },
    ],
    teaser: {
      target: "moto",
      img: "/media/moto-scenic.jpg",
      alt: "Motorrijder in een haarspeldbocht op een bergpas",
      kicker: "Ook voor motorrijders",
      kickerIcon: Bike,
      title: "Bochten. Geen snelwegen.",
      text: "Bochtige routes met bochtigheids-score, tankstops op tijd en een moto-paklijst.",
      cta: "Bekijk VoltRoute voor moto",
      accentText: "text-[#38BDF8]",
    },
    compareTitle: <>Polarsteps legt vast. ABRP plant laadstops. Tricount rekent af.<br className="hidden sm:block" /> <span className="text-[#74C69D]">VoltRoute doet het allemaal.</span></>,
    compareSub: "De enige app die je elektrische roadtrip plant, begeleidt, vastlegt én afrekent — in één deelbaar reisverhaal.",
    testimonials: [TESTIMONIALS.sofie, TESTIMONIALS.janssens, TESTIMONIALS.peter, TESTIMONIALS.kris],
    footerIcon: BatteryCharging,
    footerTagline: "VoltRoute — elektrisch op roadtrip, praktisch en rustig.",
  },
  moto: {
    accentText: "text-[#38BDF8]",
    btn: "bg-[#0284C7] hover:bg-[#0369A1]",
    heroGlow: "shadow-[0_0_25px_rgba(56,189,248,0.25)] hover:shadow-[0_0_40px_rgba(56,189,248,0.4)]",
    meta: ["VoltRoute — Roadtrip-planner voor motorrijders", "Bochtige routes, tankstops, overnachtingen, budget en paklijst in één keer gepland — gemaakt voor motorrijders. Familie volgt live mee via het reisdagboek. Start gratis."],
    kicker: "Op de moto · bochtige routes · tankstops op tijd",
    phrases: ["Plan je moto-roadtrip.", "Bochten. Geen snelwegen.", "Tanken vóór het moet.", "Rij. Lean. Herbeleef."],
    sub: "Bochtige routes, tankstops, overnachtingen, budget en reisdagboek — alles in één plan, gemaakt voor motorrijders.",
    trust: (fmt, stats) => `4.8/5 — vertrouwd door ${fmt(stats.riders)} moto- & EV-rijders · geen creditcard nodig`,
    stats: (fmt, stats) => [[fmt(stats.trips), "trips gepland"], ["5★", "bochtigheids-score per rijdag"], [fmt(stats.vehicles), "moto- & EV-modellen in database"], ["4.8/5", "gemiddelde score"]],
    showcase: {
      testid: "cfmoto-showcase",
      img: "/media/cfmoto-scenic.jpg",
      alt: "CFMoto MT 800 Explorer op een bergpas in de schemering",
      kicker: "CFMoto MT 800 Explorer · en honderden andere motoren",
      title: "Jouw moto. Jouw bochten.",
      text: "Van CFMoto MT 800 Explorer tot BMW GS — VoltRoute plant met jouw tankbereik en rijstijl de mooiste bochtige dagritten.",
      cta: "Check of jouw moto erbij staat",
      curvePill: true,
    },
    features: [
      {
        id: "bochten",
        kicker: "Bochtige routes",
        title: "De mooiste weg. Niet de snelste.",
        text: "Zet je voorkeur op 'bochtig' en VoltRoute plant via de mooiste bergwegen en passen — met een bochtigheids-score in sterren per rijdag.",
        img: "/media/moto-scenic.jpg",
        align: "left",
      },
      {
        id: "tankstops",
        kicker: "Tankstops & weer",
        title: "Tanken vóór het moet.",
        text: "Tankstops automatisch gepland op jouw tankbereik, plus regenwaarschuwing per rijdag — zodat je regenpak bovenop ligt vóór de bui valt.",
        img: "/media/moto-fuel.jpg",
        align: "right",
      },
      ...SHARED_FEATURES,
    ],
    bento: [
      SHARED_BENTO[0],
      { icon: Zap, title: "Ook elektrisch?", text: "Plan met dezelfde app je EV-roadtrip — laadstops via 250.000+ echte laadpunten." },
      SHARED_BENTO[1],
      { icon: Euro, title: "Budget & benzinekosten", text: "Raming vooraf, werkelijke tankkosten bijhouden per dag." },
      { icon: ListChecks, title: "Paklijst op maat", text: "Op basis van tripduur, seizoen en reisgezelschap, incl. moto-checklist onder het zadel." },
      { icon: CloudSun, title: "Regen per rijdag", text: "Regenwaarschuwing per etappe — regenpak klaar vóór de bui valt." },
      SHARED_BENTO[2],
      SHARED_BENTO[3],
      SHARED_BENTO[4],
      { icon: Fuel, title: "Volgende tankstop in beeld", text: "De ETA-balk op de kaart toont live je volgende tankstop — naam, afstand en resterend bereik." },
    ],
    teaser: {
      target: "ev",
      img: "/media/charging-scenic.png",
      alt: "EV aan de lader op een schilderachtige plek",
      kicker: "Ook elektrisch onderweg?",
      kickerIcon: Zap,
      title: "Laden waar het jou uitkomt.",
      text: "Slimme laadstops via 250.000+ echte laadpunten, afgestemd op jouw EV.",
      cta: "Bekijk VoltRoute voor EV",
      accentText: "text-[#74C69D]",
    },
    compareTitle: <>Polarsteps legt vast. Calimoto plant bochten. Tricount rekent af.<br className="hidden sm:block" /> <span className="text-[#38BDF8]">VoltRoute doet het allemaal.</span></>,
    compareSub: "De enige app die je moto-roadtrip plant, begeleidt, vastlegt én afrekent — in één deelbaar reisverhaal.",
    testimonials: [TESTIMONIALS.kris, TESTIMONIALS.jef, TESTIMONIALS.sofie, TESTIMONIALS.peter],
    footerIcon: Bike,
    footerTagline: "VoltRoute — op roadtrip, praktisch en rustig. Elektrisch of op de moto.",
  },
};

const readInitialMode = () => {
  const p = new URLSearchParams(window.location.search).get("mode");
  if (p === "moto" || p === "ev") return p;
  const s = localStorage.getItem("vr-landing-mode");
  return s === "moto" || s === "ev" ? s : null;
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [spots, setSpots] = useState(137);
  const [stats, setStats] = useState({ trips: 2400, riders: 2400, vehicles: 1282 });
  const [mode, setModeState] = useState(readInitialMode);
  const [gateOpen, setGateOpen] = useState(() => readInitialMode() === null);
  const fmt = (n) => n.toLocaleString("nl-NL");
  const cta = user ? "/dashboard" : "/auth";
  const heroVideoRef = useRef(null);
  const m = MODES[mode || "ev"];
  usePageMeta(m.meta[0], m.meta[1]);

  const setMode = (next) => {
    setModeState(next);
    localStorage.setItem("vr-landing-mode", next);
    setGateOpen(false);
    window.history.replaceState(null, "", next === "moto" ? "/?mode=moto" : "/");
  };
  const switchFromTeaser = (next) => {
    setMode(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("mode");
    if (p === "moto" || p === "ev") localStorage.setItem("vr-landing-mode", p);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => heroVideoRef.current?.play().catch(() => {}), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/billing/launch-spots").then((r) => setSpots(r.data.remaining)).catch(() => {});
    api.get("/stats/landing").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const shareMotoPage = async () => {
    const url = `${window.location.origin}/moto`;
    const text = "Bochten. Geen snelwegen. VoltRoute plant je moto-roadtrip: bochtige routes met bochtigheids-score, tankstops op tijd en een reisdagboek voor het thuisfront. 🏍️";
    if (navigator.share) {
      try { await navigator.share({ title: "VoltRoute voor motorrijders", text, url }); } catch (e) {}
      return;
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link + tekst gekopieerd — plak in je motorforum of groep!");
    } catch (e) {
      toast.error("Kopiëren lukte niet — de link is: " + url);
    }
  };

  const choosePlan = async (planId) => {
    if (planId === "free") { navigate(cta); return; }
    if (!user) { navigate(`/auth?plan=${planId}`); return; }
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#1B263B] text-[#f4f1ea]">
      {/* MODE GATE — eerste bezoek */}
      {gateOpen && (
        <div data-testid="mode-gate" className="fixed inset-0 z-[70] bg-[#0D1420]/85 backdrop-blur-md flex items-center justify-center px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl w-full text-center">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">Welkom bij VoltRoute</p>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-white tracking-tighter mb-8">Waarmee rij jij?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button data-testid="mode-gate-ev" onClick={() => setMode("ev")}
                className="group relative h-48 sm:h-56 rounded-3xl overflow-hidden border border-white/15 hover:border-[#74C69D]/70 hover:-translate-y-1 transition-[border-color,transform] duration-300">
                <img src="/media/cyberster-scenic.png" alt="Elektrische auto" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1420]/95 via-[#0D1420]/25 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-center justify-center gap-2 text-white font-heading font-bold text-xl">
                  <Zap className="w-5 h-5 text-[#74C69D]" /> Elektrische auto
                </div>
              </button>
              <button data-testid="mode-gate-moto" onClick={() => setMode("moto")}
                className="group relative h-48 sm:h-56 rounded-3xl overflow-hidden border border-white/15 hover:border-[#38BDF8]/70 hover:-translate-y-1 transition-[border-color,transform] duration-300">
                <img src="/media/cfmoto-scenic.jpg" alt="Moto" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1420]/95 via-[#0D1420]/25 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-center justify-center gap-2 text-white font-heading font-bold text-xl">
                  <Bike className="w-5 h-5 text-[#38BDF8]" /> Moto
                </div>
              </button>
            </div>
            <p className="text-white/40 text-xs mt-6">Je kunt bovenaan de pagina altijd wisselen.</p>
          </motion.div>
        </div>
      )}

      <header data-testid="nav-bar" className="fixed top-0 inset-x-0 z-50 bg-[#1B263B]/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center"><Zap className="w-5 h-5 text-primary-foreground" /></span>
            <span className="font-heading font-extrabold text-lg tracking-tight text-white">VoltRoute</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <a href="#functies" className="hidden sm:block text-sm font-semibold text-white/60 hover:text-white transition-[color] duration-200 px-2">Functies</a>
            <a href="#vergelijk" data-testid="nav-compare-link" className="hidden sm:block text-sm font-semibold text-white/60 hover:text-white transition-[color] duration-200 px-2">Vergelijk</a>
            <a href="#prijzen" data-testid="nav-pricing-link" className="text-sm font-semibold text-white/60 hover:text-white transition-[color] duration-200 px-2">Prijzen</a>
            <ThemeToggle />
            <Link to={cta} data-testid="nav-login-link" className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-5 py-2.5 text-sm font-semibold transition-[background-color] duration-200">
              {user ? "Dashboard" : "Inloggen"}
            </Link>
          </div>
        </div>
      </header>

      {/* HERO — fullscreen looping video + typewriter */}
      <section data-testid="hero-section" className="relative h-screen flex items-center justify-center overflow-hidden">
        {mode === "moto" ? (
          <img src="/media/moto-hero.jpg" alt="Motorrijder in een bocht op een bergweg in de schemering" data-testid="hero-moto-image"
            className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <video ref={heroVideoRef} muted loop playsInline preload="auto" data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            poster="/media/cyberster-scenic.png">
            <source src="/media/hero-road.webm" type="video/webm" />
            <source src="/media/hero-road.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B263B]/70 via-[#1B263B]/30 to-[#1B263B]" />
        <div className="grain-overlay" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div data-testid="landing-mode-toggle" className="inline-flex items-center rounded-full border border-white/20 bg-[#111827]/70 backdrop-blur-xl p-1 mb-7">
              <button data-testid="mode-ev-btn" type="button" onClick={() => setMode("ev")}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-[background-color,color] duration-200 ${mode !== "moto" ? "bg-primary text-white" : "text-white/60 hover:text-white"}`}>
                <Zap className="w-4 h-4" /> EV
              </button>
              <button data-testid="mode-moto-btn" type="button" onClick={() => setMode("moto")}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-[background-color,color] duration-200 ${mode === "moto" ? "bg-[#0284C7] text-white" : "text-white/60 hover:text-white"}`}>
                <Bike className="w-4 h-4" /> Moto
              </button>
            </div>
            <p className={`font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase mb-6 ${m.accentText}`}>{m.kicker}</p>
            <Typewriter key={mode || "ev"} phrases={m.phrases} accentClass={m.accentText} />
            <p className="text-white/80 text-base md:text-lg mt-6 max-w-xl mx-auto leading-relaxed">{m.sub}</p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link to={cta} data-testid="hero-cta-button"
                className={`inline-flex items-center gap-2 rounded-full ${m.btn} text-white px-8 py-4 font-semibold ${m.heroGlow} active:scale-95 transition-[transform,background-color,box-shadow] duration-200`}>
                Start je trip gratis <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#showcase" data-testid="hero-discover-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white px-8 py-4 font-semibold backdrop-blur transition-[border-color] duration-200">
                Ontdek meer
              </a>
            </div>
            <div className="mt-8 flex items-center justify-center gap-2 text-white/60 text-sm">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" /> {m.trust(fmt, stats)}
            </div>
            {mode === "moto" && (
              <button data-testid="share-moto-page-btn" type="button" onClick={shareMotoPage}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#38BDF8]/35 bg-[#38BDF8]/10 hover:border-[#38BDF8] text-[#7DD3FC] px-5 py-2.5 text-sm font-semibold transition-[border-color] duration-200">
                <Share2 className="w-4 h-4" /> Deel de moto-pagina met je rijgroep
              </button>
            )}
          </motion.div>
        </div>
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/50">
          <ArrowDown className="w-5 h-5" />
        </motion.div>
      </section>

      {/* STATS BAR */}
      <section data-testid="stats-bar" className="border-b border-white/10 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {m.stats(fmt, stats).map(([v, l]) => (
            <div key={l}>
              <p className={`font-mono font-extrabold text-2xl sm:text-3xl ${m.accentText}`}>{v}</p>
              <p className="text-xs text-white/50 mt-1.5">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VEHICLE SHOWCASE — MG Cyberster (EV) / CFMoto MT 800 Explorer (Moto) */}
      <section id="showcase" data-testid={m.showcase.testid} className="relative h-screen overflow-hidden flex items-end">
        <motion.img key={m.showcase.img} src={m.showcase.img} alt={m.showcase.alt}
          initial={{ scale: 1.08 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B263B] via-transparent to-[#1B263B]/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-4 ${m.accentText}`}>{m.showcase.kicker}</p>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-white tracking-tighter max-w-xl leading-tight">{m.showcase.title}</h2>
            <p className="text-white/75 mt-4 max-w-md leading-relaxed">{m.showcase.text}</p>
            {m.showcase.curvePill && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#38BDF8]/30 bg-[#38BDF8]/10 px-4 py-2" data-testid="moto-curve-score">
                <span className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />)}</span>
                <span className="text-[#BAE6FD] text-xs font-semibold">bochtigheids-score per rijdag</span>
              </div>
            )}
            <div>
              <Link to={cta} data-testid="showcase-cta-link" className={`inline-flex items-center gap-2 mt-6 font-semibold hover:gap-3 transition-[gap] duration-200 ${m.accentText}`}>
                {m.showcase.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESLA-STYLE FULLSCREEN FEATURE SECTIONS */}
      <div id="functies">
        {m.features.map((f) => (
          <section key={f.id} data-testid={`feature-section-${f.id}`} className="relative min-h-screen overflow-hidden flex items-center">
            <img src={f.img} alt={f.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className={`absolute inset-0 ${f.align === "left" ? "bg-gradient-to-r" : "bg-gradient-to-l"} from-[#1B263B]/90 via-[#1B263B]/40 to-transparent`} />
            <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
                className={`max-w-lg ${f.align === "right" ? "ml-auto text-right" : ""}`}>
                <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-4 ${m.accentText}`}>{f.kicker}</p>
                <h2 className="font-heading font-light text-4xl md:text-5xl text-white tracking-tighter leading-tight">{f.title}</h2>
                <p className="text-white/75 mt-5 leading-relaxed">{f.text}</p>
                <Link to={cta} className={`inline-flex items-center gap-2 mt-6 font-semibold hover:gap-3 transition-[gap] duration-200 ${m.accentText} ${f.align === "right" ? "flex-row-reverse" : ""}`}>
                  Probeer het gratis <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </section>
        ))}
      </div>

      {/* CROSS-PROMO TEASER — wissel van modus */}
      <section data-testid="mode-teaser" className="relative overflow-hidden">
        <img src={m.teaser.img} alt={m.teaser.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1B263B]/75" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 flex flex-col sm:flex-row sm:items-center gap-8">
          <div className="flex-1">
            <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-3 flex items-center gap-2 ${m.teaser.accentText}`}>
              <m.teaser.kickerIcon className="w-4 h-4" /> {m.teaser.kicker}
            </p>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-white tracking-tighter leading-tight">{m.teaser.title}</h2>
            <p className="text-white/70 mt-3 max-w-md leading-relaxed">{m.teaser.text}</p>
          </div>
          <button data-testid="mode-teaser-cta" type="button" onClick={() => switchFromTeaser(m.teaser.target)}
            className={`self-start sm:self-center inline-flex items-center gap-2 rounded-full border px-7 py-3.5 font-semibold hover:gap-3 transition-[gap,border-color] duration-200 ${m.teaser.accentText} ${m.teaser.target === "moto" ? "border-[#38BDF8]/40 hover:border-[#38BDF8]" : "border-[#74C69D]/40 hover:border-[#74C69D]"}`}>
            {m.teaser.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* BENTO GRID — remaining features */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-4 ${m.accentText}`}>En nog veel meer</p>
        <h2 className="font-heading font-light text-3xl sm:text-4xl tracking-tighter text-white mb-14 max-w-lg">Alles voor je trip, in één app.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {m.bento.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07, duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7 hover:bg-white/[0.07] hover:-translate-y-1 transition-[transform,background-color] duration-300">
              <span className={`w-11 h-11 rounded-full flex items-center justify-center mb-5 ${mode === "moto" ? "bg-[#38BDF8]/12" : "bg-[#74C69D]/12"}`}>
                <f.icon className={`w-5 h-5 ${m.accentText}`} />
              </span>
              <h3 className="font-heading font-bold text-lg text-white mb-1.5">{f.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-xs text-white/40 mt-10 flex items-center gap-2">
          <ShieldCheck className={`w-4 h-4 ${m.accentText}`} /> Gebouwd op betrouwbare open data: OpenChargeMap, Open-EV-Data, OpenStreetMap & Open-Meteo — door reizigers, voor reizigers.
        </p>
      </section>

      {/* SHOWCASE — publieke voorbeeld-trip */}
      <ShowcaseTrip />

      {/* COMPARISON */}
      <section id="vergelijk" className="bg-[#111827] py-28">
        <div className="max-w-5xl mx-auto px-6">
          <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-4 ${m.accentText}`}>Waarom VoltRoute</p>
          <h2 className="font-heading font-light text-3xl sm:text-4xl tracking-tighter text-white mb-4 max-w-2xl">{m.compareTitle}</h2>
          <p className="text-white/60 mb-12 max-w-xl">{m.compareSub}</p>
          <ComparisonTable mode={mode === "moto" ? "moto" : "ev"} />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials" className="max-w-7xl mx-auto px-6 py-28">
        <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-4 ${m.accentText}`}>Wat reizigers zeggen</p>
        <h2 className="font-heading font-light text-3xl sm:text-4xl tracking-tighter text-white mb-14 max-w-lg">Duizenden kilometers, nul stress.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {m.testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7">
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}</div>
              <p className="text-white/85 text-sm leading-relaxed mb-5">"{t.text}"</p>
              <p className="text-white font-semibold text-sm">{t.name}</p>
              <p className="text-white/45 text-xs mt-0.5">{t.car}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="prijzen" data-testid="pricing-section" className="bg-[#111827] py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-6">
            <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-4 ${m.accentText}`}>Prijzen</p>
            <h2 className="font-heading font-light text-3xl sm:text-4xl tracking-tighter text-white">Kies het plan dat bij jouw reis past</h2>
          </div>
          <div className="flex justify-center mb-14">
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="scarcity-banner">
              <Flame className="w-4 h-4" />
              {spots > 0 ? (
                <>Lanceeractie: de eerste 500 gebruikers houden de Pro-prijs voor altijd — nog <span data-testid="launch-spots-count" className="font-mono font-extrabold">{spots}</span> plekken</>
              ) : (
                <>De 500 lanceerplekken zijn vergeven — de huidige Pro-prijs geldt nog steeds</>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
            {plans.map((p) => {
              const highlight = p.id === "pro";
              return (
                <div key={p.id} data-testid={`plan-card-${p.id}`}
                  className={`rounded-2xl p-8 flex flex-col relative ${highlight ? "bg-primary text-primary-foreground shadow-[0_0_50px_rgba(45,106,79,0.45)] md:-translate-y-3 border border-[#74C69D]/40" : "border border-white/10 bg-white/[0.04] text-white"}`}>
                  {highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-[#1B263B] text-xs font-extrabold rounded-full px-4 py-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> MEEST GEKOZEN
                    </span>
                  )}
                  <h3 className="font-heading font-extrabold text-xl">{p.name}</h3>
                  <p className={`text-xs mt-1 ${highlight ? "text-primary-foreground/75" : "text-white/50"}`}>{p.tagline}</p>
                  <p className="font-mono font-extrabold text-4xl mt-5">
                    {p.price === 0 ? "€0" : `€${p.price.toFixed(2).replace(".", ",")}`}
                    {p.price > 0 && <span className="text-sm font-normal opacity-70"> /maand</span>}
                  </p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 ${highlight ? "text-[#B7E4C7]" : "text-[#74C69D]"}`} /> {f}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`plan-cta-${p.id}`} onClick={() => choosePlan(p.id)}
                    className={`mt-8 rounded-full py-3.5 text-sm font-bold active:scale-95 transition-[transform,background-color] duration-200 ${highlight ? "bg-white text-primary hover:bg-[#F0F7F4]" : "bg-primary text-primary-foreground hover:bg-[#1B4332]"}`}>
                    {p.id === "free" ? "Start gratis" : `Kies ${p.name}`}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-sm text-white/50 mt-10 flex items-center justify-center gap-2">
            <ShieldCheck className={`w-4 h-4 ${m.accentText}`} /> 30 dagen niet-goed-geld-terug · maandelijks opzegbaar · veilig betalen via Mollie
          </p>
        </div>
      </section>

      {/* FOOTER CTA — massive typography */}
      <section data-testid="footer-cta" className="relative overflow-hidden py-32 text-center">
        <div className="grain-overlay" />
        <div className="relative max-w-6xl mx-auto px-6">
          <p className={`font-mono text-[11px] tracking-[0.3em] uppercase mb-6 ${m.accentText}`}>Klaar voor vertrek?</p>
          <h2 className="font-heading font-light text-[11vw] sm:text-[8vw] lg:text-7xl text-white tracking-tighter leading-none mb-10">
            Jouw avontuur<br /><span className={m.accentText}>start hier.</span>
          </h2>
          <Link to={cta} data-testid="footer-cta-button"
            className={`inline-flex items-center gap-2 rounded-full ${m.btn} text-white px-10 py-5 font-bold text-lg ${m.heroGlow} active:scale-95 transition-[transform,background-color] duration-200`}>
            <m.footerIcon className="w-5 h-5" /> Plan gratis je eerste trip
          </Link>
          <p className="text-white/40 text-sm mt-6">Binnen 2 minuten een compleet reisplan · geen creditcard nodig</p>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 text-center text-sm text-white/40">
        <p>{m.footerTagline}</p>
        <div className="flex justify-center gap-6 mt-4">
          <Link to="/wat-is-nieuw" data-testid="footer-updates-link" className="hover:text-white transition-[color] duration-200">Wat is nieuw & roadmap</Link>
          <Link to="/routes" data-testid="footer-routes-link" className="hover:text-white transition-[color] duration-200">Route-bibliotheek</Link>
          <Link to="/hoe-werkt-het" data-testid="footer-help-link" className="hover:text-white transition-[color] duration-200">Hoe werkt het?</Link>
        </div>
      </footer>

      <ExitIntentPopup />
      <StickyMobileCta to={cta} btnCls={m.btn} />
    </div>
  );
}
